/**
 * @file Homepage.jsx
 * @description Modern Homepage with Tailwind CSS
 */

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Slider from '../components/Slider';
import GoogleReviews from '../components/GoogleReviews';
import IMAGES from '../config/images';
import { pricingData } from '../data/pricing';

const Homepage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const serviceHighlights = [
    {
      title: 'Plumbing Services',
      description: 'Expert plumbing repairs, installations, and maintenance',
      icon: '🔧',
      image: IMAGES.plumbingHome,
      link: '/services',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Gas Services',
      description: 'Gas Safe registered engineers for all gas work',
      icon: '🔥',
      image: IMAGES.gasHome,
      link: '/services',
      gradient: 'from-orange-500 to-red-600'
    },
    {
      title: 'Heating Services',
      description: 'Boiler installation, servicing, and heating systems',
      icon: '♨️',
      image: IMAGES.heatingHome,
      link: '/services',
      gradient: 'from-red-500 to-pink-600'
    },
    {
      title: 'Emergency Services',
      description: '24/7 emergency callout service across London',
      icon: '🚨',
      image: IMAGES.emergencyHome,
      link: '/emergency',
      gradient: 'from-red-600 to-red-700'
    }
  ];

  const features = [
    { icon: '⚡', title: 'Fast Response', desc: 'Quick response times with 24/7 emergency service available' },
    { icon: '🎓', title: 'Expert Engineers', desc: 'Gas Safe registered, fully qualified professionals' },
    { icon: '💰', title: 'Transparent Pricing', desc: 'Clear, competitive pricing with no hidden charges' }
  ];

  const areas = [
    'The Hyde', 'Finchley', 'Stanmore', 'Wembley', 'Colindale', 'Harrow',
    'Cricklewood', 'Park Royal', 'North Finchley', 'Neasden', 'Willesden',
    'Notting Hill', 'Wembley Park', 'Maida Vale', 'West Hendon', 'Brent Cross',
    'Harlesden', 'Sudbury Hill', 'Edgware', 'Burnt Oak'
  ];

  return (
    <div className="homepage">
      <Helmet>
        <title>Metro Plumbing & Heating | Professional Plumbing & Gas Services London</title>
        <meta
          name="description"
          content="Metro Plumbing & Heating - Professional plumbing, gas, and heating services across London. Gas Safe registered. 24/7 emergency callouts. Competitive rates."
        />
      </Helmet>

      {/* Hero Section */}
      <Slider />

      {/* Services Overview */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional plumbing, gas, and heating solutions for homes and businesses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {serviceHighlights.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="group card overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = '/images/service-placeholder.jpg';
                    }}
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${service.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                  <div className="absolute top-4 right-4 text-4xl animate-bounce-slow">{service.icon}</div>
                  {/* Decorative corner */}
                  <div className="absolute top-0 left-0 w-16 h-16 bg-white opacity-20 rounded-br-full"></div>
                  <div className="absolute bottom-0 right-0 w-20 h-20 bg-black opacity-10 rounded-tl-full"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <span className="text-primary-600 font-semibold flex items-center group-hover:translate-x-2 transition-transform duration-300">
                    Learn More
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            <Link
              to="/services"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold text-lg shadow-lg hover:bg-primary-700 hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
            >
              <span>View All Services</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <GoogleReviews />

      {/* Metro Banner Section */}
      <section className="py-0 bg-gray-50 overflow-hidden">
        <div className="relative h-auto md:h-96 lg:h-[500px] group">
          <img
            src={IMAGES.metroBanner}
            alt="Metro Plumbing & Heating - Professional Services"
            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 hidden md:block"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="md:absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/90 to-primary-700/85 md:from-primary-900/80 md:via-primary-800/60 md:to-transparent flex items-center py-12 md:py-0">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-2xl animate-fade-in-up">
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4 drop-shadow-lg leading-tight">
                  Professional Service You Can Trust
                </h2>
                <p className="text-base sm:text-lg md:text-xl text-white/95 md:text-white/90 mb-6 drop-shadow leading-relaxed">
                  Serving London with excellence since 2008. Gas Safe registered engineers available 24/7 for all your plumbing, gas, and heating needs.
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                  <a
                    href="tel:+447379787766"
                    className="flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-primary-600 rounded-lg font-bold hover:bg-gray-100 transform hover:-translate-y-1 transition-all duration-300 shadow-lg text-base sm:text-lg"
                  >
                    <span className="text-xl">📞</span>
                    <span>Call Now</span>
                  </a>
                  <Link
                    to="/get-quote"
                    className="flex items-center justify-center px-6 py-3.5 bg-primary-500 md:bg-primary-600 text-white rounded-lg font-bold hover:bg-primary-600 md:hover:bg-primary-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg text-base sm:text-lg"
                  >
                    Get Free Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up relative">
              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 bg-green-500 text-white px-6 py-3 rounded-full shadow-lg z-10 animate-bounce-slow">
                <div className="text-center">
                  <div className="text-2xl font-bold">15+</div>
                  <div className="text-xs">Years</div>
                </div>
              </div>
              <img
                src={IMAGES.aboutMetro}
                alt="Metro Plumbing & Heating Team"
                className="rounded-2xl shadow-2xl w-full h-auto"
                onError={(e) => {
                  e.target.src = IMAGES.aboutPlaceholder;
                }}
              />
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                About Metro Plumbing & Heating
              </h2>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                Metro Plumbing & Heating is London's trusted provider of professional
                plumbing, gas, and heating services. With years of experience serving
                residential and commercial clients across London, we pride ourselves on
                delivering quality workmanship and excellent customer service.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                All our engineers are Gas Safe registered and fully insured, ensuring
                every job is completed to the highest standards. From routine maintenance
                to emergency callouts, we're here to help 24/7.
              </p>
              <ul className="space-y-3 mb-8">
                {['Gas Safe Registered Engineers', '24/7 Emergency Service', 'Competitive Pricing', 
                  'Fully Insured', 'Free Quotes', 'Excellent Customer Reviews'].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3 text-gray-700 group">
                    <span className="flex-shrink-0 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center text-white text-sm relative">
                      <span className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75"></span>
                      <span className="relative">✓</span>
                    </span>
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/get-quote"
                className="inline-block px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Get a Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
        {/* Decorative shapes */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-primary-200 rounded-full opacity-20 animate-pulse-slow"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-primary-300 rounded-full opacity-20 animate-bounce-slow"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Metro Plumbing & Heating?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-6xl mb-4 animate-bounce-slow">{feature.icon}</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h4>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Areas We Cover</h2>
            <p className="text-lg text-gray-600">Serving all of London and surrounding areas</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {areas.map((area, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-4 md:p-6 text-center hover:from-primary-100 hover:to-primary-200 transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-lg animate-fade-in-up"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <h5 className="font-bold text-primary-700 text-sm md:text-base">{area}</h5>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Rates</h2>
            <p className="text-lg text-gray-600">Transparent, competitive pricing for all services</p>
            <p className="text-sm text-primary-600 font-semibold mt-2">Zone 1 locations: Add £30 to all prices</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Standard Rates */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 animate-fade-in-up relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-20 h-20 bg-blue-300 rounded-full opacity-20 -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
              <h3 className="text-xl font-bold text-blue-900 mb-4 pb-3 border-b-2 border-blue-300">Standard Rates</h3>
              <div className="space-y-3">
                {pricingData.standard.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">{item.service}</span>
                    <span className="text-blue-600 font-bold text-lg">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Evening Rates */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 animate-fade-in-up relative overflow-hidden group" style={{ animationDelay: '0.1s' }}>
              <div className="absolute top-0 right-0 w-20 h-20 bg-purple-300 rounded-full opacity-20 -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
              <h3 className="text-xl font-bold text-purple-900 mb-4 pb-3 border-b-2 border-purple-300">Evening (6pm-10pm)</h3>
              <div className="space-y-3">
                {pricingData.evening.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">{item.service.replace(' (6pm-10pm)', '')}</span>
                    <span className="text-purple-600 font-bold text-lg">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Weekend Rates */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 animate-fade-in-up relative overflow-hidden group" style={{ animationDelay: '0.2s' }}>
              <div className="absolute top-0 right-0 w-20 h-20 bg-green-300 rounded-full opacity-20 -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
              <h3 className="text-xl font-bold text-green-900 mb-4 pb-3 border-b-2 border-green-300">Weekend</h3>
              <div className="space-y-3">
                {pricingData.weekend.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">{item.service.replace('Weekend ', '').replace('Plumbing', 'Plumbing (Sat)').replace('Gas', 'Gas (Sat)')}</span>
                    <span className="text-green-600 font-bold text-lg">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Emergency Rates */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 animate-fade-in-up border-2 border-red-300 relative overflow-hidden group" style={{ animationDelay: '0.3s' }}>
              <div className="absolute top-0 right-0 w-20 h-20 bg-red-300 rounded-full opacity-20 -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
              <h3 className="text-xl font-bold text-red-900 mb-4 pb-3 border-b-2 border-red-300">Emergency / Special</h3>
              <div className="space-y-3">
                {pricingData.emergency.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">{item.service.replace(' (10pm-7am)', '')}</span>
                    <span className="text-red-600 font-bold text-lg">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-8 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <Link
              to="/get-quote"
              className="inline-block px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute w-96 h-96 bg-white rounded-full -top-20 -right-20 animate-pulse-slow"></div>
          <div className="absolute w-64 h-64 bg-white rounded-full bottom-10 left-10 animate-bounce-slow"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-fade-in-up">
            Need a Plumber or Heating Engineer?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            Get in touch today for a free no obligation quote or emergency assistance
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <a
              href="tel:+447379787766"
              className="px-8 py-4 bg-danger-600 text-white rounded-lg font-bold text-lg shadow-xl hover:bg-danger-700 hover:shadow-2xl transform hover:-translate-y-1 hover:scale-110 transition-all duration-300 relative overflow-hidden group"
            >
              <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
              <span className="relative">
              📞 Call Now: 07379 787766
              </span>
            </a>
            <Link
              to="/get-quote"
              className="px-8 py-4 bg-white text-primary-600 rounded-lg font-bold text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
